import * as React from "react";
import { cn } from "@/lib/utils";

interface InfoRowProps {
  /** 라벨 텍스트 */
  label: string;
  /** 값 (문자열, 숫자, 또는 React 노드) */
  value: React.ReactNode;
  /** 레이아웃 형태 */
  layout?: "spread" | "inline";
  /** 추가 클래스명 */
  className?: string;
  /** 라벨 클래스명 */
  labelClassName?: string;
  /** 값 클래스명 */
  valueClassName?: string;
}

/**
 * InfoRow - 라벨-값 쌍을 표시하는 컴포넌트
 *
 * @example
 * // spread 레이아웃 (기본): 라벨 왼쪽, 값 오른쪽
 * <InfoRow label="이메일" value={user.email} />
 *
 * // inline 레이아웃: "라벨: 값" 형태
 * <InfoRow label="이메일" value={user.email} layout="inline" />
 */
const InfoRow = React.forwardRef<HTMLDivElement, InfoRowProps>(
  ({ label, value, layout = "spread", className, labelClassName, valueClassName }, ref) => {
    if (layout === "inline") {
      return (
        <p ref={ref as React.Ref<HTMLParagraphElement>} className={cn("text-sm", className)}>
          <strong className={cn("font-medium", labelClassName)}>{label}:</strong>{" "}
          <span className={valueClassName}>{value}</span>
        </p>
      );
    }

    return (
      <div ref={ref} className={cn("flex justify-between text-sm", className)}>
        <span className={cn("text-gray-500", labelClassName)}>{label}</span>
        <span className={cn("font-medium", valueClassName)}>{value}</span>
      </div>
    );
  }
);

InfoRow.displayName = "InfoRow";

interface InfoDisplayProps {
  /** 표시할 항목들 */
  items: Array<{
    label: string;
    value: React.ReactNode;
    hidden?: boolean;
  }>;
  /** 레이아웃 형태 */
  layout?: "spread" | "inline";
  /** 추가 클래스명 */
  className?: string;
  /** 세로 간격 */
  spacing?: "tight" | "normal" | "loose";
}

/**
 * InfoDisplay - 여러 InfoRow를 그룹으로 표시하는 컴포넌트
 *
 * @example
 * <InfoDisplay
 *   items={[
 *     { label: "이름", value: user.name },
 *     { label: "이메일", value: user.email },
 *     { label: "전화번호", value: user.phone, hidden: !user.phone },
 *   ]}
 * />
 */
const InfoDisplay = React.forwardRef<HTMLDivElement, InfoDisplayProps>(
  ({ items, layout = "spread", className, spacing = "normal" }, ref) => {
    const spacingClass = {
      tight: "space-y-1",
      normal: "space-y-2",
      loose: "space-y-3",
    }[spacing];

    const visibleItems = items.filter((item) => !item.hidden);

    return (
      <div ref={ref} className={cn(spacingClass, className)}>
        {visibleItems.map((item, index) => (
          <InfoRow key={index} label={item.label} value={item.value} layout={layout} />
        ))}
      </div>
    );
  }
);

InfoDisplay.displayName = "InfoDisplay";

export { InfoRow, InfoDisplay };
